import { useEffect, useRef, useState } from "react";

import { Check, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { TableColumnMeta } from "@/types/table";

/** `ColumnFilter` jadval ustunidan faqat shularni o'qiydi. */
interface FilterColumn {
  getFilterValue: () => unknown;
  setFilterValue: (value: unknown) => void;
  columnDef: { meta?: TableColumnMeta };
}

/**
 * Ustun sarlavhasidagi filtr. Qaysi boshqaruv chizilishi `meta.filterVariant`
 * ga bog'liq — jadval filtrlar ro'yxatini bilmaydi, yangi ustunda faqat
 * `meta` yoziladi.
 */
export function ColumnFilter({ column, label }: { column: FilterColumn; label: string }) {
  const meta = column.columnDef.meta;
  const value = column.getFilterValue();
  const current = typeof value === "string" ? value : "";

  if (meta?.filterVariant === "text") {
    return <TextFilter label={label} value={current} onChange={column.setFilterValue} />;
  }
  if (meta?.filterVariant === "select" && meta.filterOptions) {
    return (
      <OptionList
        label={label}
        value={current}
        options={meta.filterOptions}
        onChange={column.setFilterValue}
      />
    );
  }
  return null;
}

/**
 * Matn filtri: har bosishda so'rov yubormaydi — 400 ms jim turgandan keyin
 * bir marta. Oyna ochilishining yagona sababi yozish, shuning uchun fokus
 * darhol maydonda.
 */
function TextFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [prev, setPrev] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Tashqi qiymat o'zgarsa ("Tozalash" chipi) ichkarisi ham yangilanadi.
  if (prev !== value) {
    setPrev(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft.trim() === value) return;
    const id = setTimeout(() => onChange(draft.trim()), 400);
    return () => clearTimeout(id);
  }, [draft, value, onChange]);

  useEffect(() => inputRef.current?.focus(), []);

  return (
    <div className="relative w-64 p-1.5">
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2" />
      <Input
        ref={inputRef}
        value={draft}
        aria-label={label}
        placeholder={t("table.search")}
        onChange={event => setDraft(event.target.value)}
        className="border-none pr-9 pl-9 shadow-none focus-visible:ring-0"
      />
      {draft ? (
        <button
          type="button"
          aria-label={t("table.clear")}
          onClick={() => setDraft("")}
          className="text-muted-foreground hover:bg-muted hover:text-foreground absolute top-1/2 right-3 grid size-6 -translate-y-1/2 place-items-center rounded-md"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Tanlov filtri — Select emas, ochiq ro'yxat: popover ichida ikkinchi
 * popover ochilmaydi va variantlar bir qarashda ko'rinadi. Tanlangani
 * yana bosilsa filtr olib tashlanadi.
 */
function OptionList({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <div role="listbox" aria-label={label} className="flex w-56 flex-col gap-0.5 p-1.5">
      {options.map(option => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => onChange(selected ? undefined : option.value)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors",
              selected ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted",
            )}
          >
            <span
              className={cn(
                "grid size-4 place-items-center rounded-full border transition-colors",
                selected ? "border-primary bg-primary text-primary-foreground" : "border-border",
              )}
            >
              {selected ? <Check className="size-3" /> : null}
            </span>
            {option.label}
          </button>
        );
      })}
      {value ? (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="text-muted-foreground hover:bg-muted hover:text-foreground mt-1 flex items-center justify-center gap-1.5 rounded-lg border-t px-2.5 py-1.5 text-xs"
        >
          <X className="size-3.5" />
          {t("table.clear")}
        </button>
      ) : null}
    </div>
  );
}
